/**
 * The gift send modal's words and its To box (2026-10-22, Chris; `Claude
 * outputs/gift-flow-review-2026-10-22/design/DESIGN.md` §2, §3 and "The
 * pattern: say it once"). Supersedes the 2026-10-21 lanes ("Change", "Or let
 * us send it", the under-button line, the pane line, the fine print).
 *
 * One smart To box: the circle (Coven first), "@" domain completions, a typo
 * fix, a phone number formatted as it is typed. The primary button follows
 * what is in it (the Google Docs share pattern): nobody named, the link IS the
 * action; once someone is named, Send is primary and the link steps aside.
 *
 * No pronoun for the sender or the recipient anywhere (Chris, 2026-10-22):
 * the name, or "them"/"their".
 *
 * Kept pure so the modal and its tests read the same words.
 */
import { suggestEmail, looksLikeEmail } from '@/utils/stage-two'

export function possessive (name) {
  const n = (name || '').trim() || 'them'
  return n.endsWith('s') ? `${n}'` : `${n}'s`
}

const nameOr = (name, other = 'them') => (name || '').trim() || other

// ── What was typed ───────────────────────────────────────────────────────────
const PHONE_CHARS = /^[+\d\s().-]+$/
function digitsOf (v) { return String(v || '').replace(/\D/g, '') }

/** Looks like a phone being typed: phone characters and at least three digits. */
export function isPhoneish (value) {
  const v = String(value || '').trim()
  return !!v && PHONE_CHARS.test(v) && digitsOf(v).length >= 3
}

/** 'email' | 'phone' | '' (a name, or not whole yet). */
export function contactKind (value) {
  const v = String(value || '').trim()
  if (!v) return ''
  if (looksLikeEmail(v)) return 'email'
  if (isPhoneish(v)) {
    const d = digitsOf(v)
    if (v.startsWith('+')) return d.length >= 8 && d.length <= 15 ? 'phone' : ''
    return d.length === 10 || (d.length === 11 && d[0] === '1') ? 'phone' : ''
  }
  return ''
}

/** "(415) 555-0134" as it is typed; "+1 (415) 555-0134"; another country as typed. */
export function formatPhone (value) {
  const raw = String(value || '').trim()
  if (raw.startsWith('+') && !raw.startsWith('+1')) return '+' + digitsOf(raw)
  let d = digitsOf(raw)
  let prefix = ''
  if ((raw.startsWith('+1') || (d.length === 11 && d[0] === '1'))) { prefix = '+1 '; d = d.slice(1) }
  d = d.slice(0, 10)
  if (d.length <= 3) return prefix + d
  if (d.length <= 6) return `${prefix}(${d.slice(0, 3)}) ${d.slice(3)}`
  return `${prefix}(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`
}

/** What the server gets: E.164 ("+14155550134"). */
export function phoneDigits (value) {
  const raw = String(value || '').trim()
  const d = digitsOf(raw)
  if (raw.startsWith('+')) return '+' + d
  if (d.length === 11 && d[0] === '1') return '+' + d
  return '+1' + d
}

// ── The dropdown ─────────────────────────────────────────────────────────────
export const DOMAINS = ['gmail.com', 'icloud.com', 'yahoo.com', 'outlook.com', 'hotmail.com']
const CIRCLE_FOOTER = 'Not on Mystic Aida? Type their email or mobile number.'
// Texting, 2026-10-31: we never text a number someone else typed; the sender does.
const TEXT_SUB = 'From your own phone, with the link.'

/**
 * The rows under the To box for what is typed. friends: my-friends rows
 * ({querent_uuid, nick_name, tier}).
 * Returns { kind: 'circle' | 'email' | 'phone', rows, footer }.
 */
export function toSuggestions (typed, friends, { limit = 6 } = {}) {
  const t = String(typed || '').trim()
  if (t.includes('@')) {
    const at = t.lastIndexOf('@')
    const local = t.slice(0, at + 1)
    const part = t.slice(at + 1).toLowerCase()
    const rows = at > 0
      ? DOMAINS.filter((d) => d.startsWith(part)).map((d) => ({ key: `@${d}`, kind: 'email', value: local + d, local, domain: d }))
      : []
    if (!rows.length && looksLikeEmail(t)) rows.push({ key: `@${part}`, kind: 'email', value: t, local, domain: part })
    return { kind: 'email', rows, footer: '' }
  }
  if (isPhoneish(t)) {
    const shown = formatPhone(t)
    return { kind: 'phone', rows: [{ key: 'phone', kind: 'phone', label: `Text ${shown}`, value: shown, sub: TEXT_SUB }], footer: '' }
  }
  const q = t.toLowerCase()
  const all = (friends || []).filter((f) => f && f.querent_uuid && (f.nick_name || '').trim())
  const hits = q ? all.filter((f) => f.nick_name.toLowerCase().includes(q)) : all
  const coven = hits.filter((f) => f.tier === 'coven')
  const rest = hits.filter((f) => f.tier !== 'coven')
  const rows = [...coven, ...rest].slice(0, limit).map((f) => {
    const isCoven = f.tier === 'coven'
    return { key: f.querent_uuid, kind: 'member', name: f.nick_name.trim(), uuid: f.querent_uuid, coven: isCoven,
             sub: `${isCoven ? 'Coven' : 'Friend'} · goes to their Messages` }
  })
  return { kind: 'circle', rows, footer: CIRCLE_FOOTER }
}

/** "Did you mean linda.lee@gmail.com?" (feedback: always shown). '' when it looks right. */
export function domainHint (typed) {
  const t = String(typed || '').trim()
  if (!t.includes('@')) return ''
  return suggestEmail(t)
}

/** The token a picked row (or a typed contact) becomes: { kind, label, uuid, value }. */
export function toTokenFor (row) {
  if (!row) return null
  if (row.kind === 'member') return { kind: 'member', label: row.name, uuid: row.uuid, value: '' }
  if (row.kind === 'phone') return { kind: 'phone', label: formatPhone(row.value), uuid: '', value: phoneDigits(row.value) }
  const v = String(row.value || '').trim()
  return { kind: 'email', label: v, uuid: '', value: v }
}

// ── The buttons ──────────────────────────────────────────────────────────────
/**
 * The primary button. lane: 'share' (nobody named, a phone) | 'copy' (nobody
 * named, a computer) | 'member' | 'email' | 'phone'. done: '' | 'sent' | 'copied'.
 * phone: this device is a phone. A typed phone number is texted by the sender
 * (texting, 2026-10-31): a phone opens Messages to it, a computer copies the link.
 */
export function primaryFor (lane, name, { done = '', phone = false } = {}) {
  const n = nameOr(name)
  if (lane === 'phone') {
    if (done === 'copied') return { label: 'Copied', icon: '', done: true }
    return phone
      ? { label: `Text it to ${n}`, icon: 'fa-comment', done: false }
      : { label: `Copy ${possessive(n)} link`, icon: 'fa-link', done: false }
  }
  if (lane === 'member' || lane === 'email') {
    if (done === 'sent') return { label: 'Sent', icon: '', done: true }
    if (lane === 'member') return { label: `Send to ${n}`, icon: 'fa-gift', done: false }
    return { label: `Email it to ${n}`, icon: 'fa-envelope', done: false }
  }
  if (done === 'copied') return { label: 'Copied', icon: '', done: true }
  if (lane === 'share') return { label: `Share with ${n}`, icon: 'fa-arrow-up-from-bracket', done: false }
  return { label: `Copy ${possessive(n)} link`, icon: 'fa-link', done: false }
}

/** The secondary, left of the lock pill: only once someone is named. */
export function secondaryFor (lane, { phone = false, done = '' } = {}) {
  if (lane !== 'member' && lane !== 'email' && lane !== 'phone') return null
  // A typed phone on a computer: the primary already copies.
  if (lane === 'phone' && !phone) return null
  if (done === 'copied') return { label: 'Copied', icon: 'fa-check', share: false }
  return phone
    ? { label: 'Share', icon: 'fa-arrow-up-from-bracket', share: true }
    : { label: 'Copy link instead', icon: 'fa-link', share: false }
}

/** The promise that protects: five words, always shown. */
export function lockLine (name) { return `Only ${nameOr(name)} can open it` }

/** After a copy, as feedback. */
export function copiedLine (name, { lane = '' } = {}) {
  return lane === 'phone' ? `Now paste it into a text to ${nameOr(name)}.` : `Now paste it wherever you talk to ${nameOr(name)}.`
}

/** The end of the preview sheet. */
export function everythingLine (name) {
  return `${nameOr(name, 'They')} receives this and nothing else. Your own conversation with Mystic Aida stays with you.`
}

/** "How sending works", behind ⓘ (DESIGN "The pattern", word for word). */
export function HELP_LINES (name, { wide = true } = {}) {
  const n = nameOr(name)
  return [
    { key: 'what', icon: 'fa-envelope-open-text',
      text: wide
        ? `On the left is exactly what ${n} opens. Your own conversation with Mystic Aida stays with you.`
        : `"Read it as they will" shows exactly what ${n} opens. Your own conversation with Mystic Aida stays with you.` },
    { key: 'to', icon: 'fa-user-group',
      text: "Pick someone from your circle and it goes to their Messages. An email gets it from us, with one reminder if it isn't opened. A mobile number: you text them the link from your own phone." },
    { key: 'note', icon: 'fa-pen',
      text: 'Mystic Aida drafted your note from the reading. Change any word; tap your name to sign it another way.' },
    { key: 'lock', icon: 'fa-lock', text: `Only ${n} can open it. You can take it back from your Journal until they do.` },
  ]
}

/** The first-time hints, inline only on the first two opens. */
export const TO_HINT = 'Pick someone from your circle and it goes to their Messages. Anyone else: type an email or mobile number, or share the link yourself.'

// ── First-time hints: the first two opens, per browser ───────────────────────
export const HINTS_KEY = 'ma_gift_hints'
const HINT_OPENS = 2
function seenCount () {
  try { return Number(window.localStorage.getItem(HINTS_KEY)) || 0 } catch (e) { return 0 }
}
/** True on the first two opens of the send modal in this browser (empty storage = first time). */
export function hintsDue () { return seenCount() < HINT_OPENS }
/** One more open. Never throws. */
export function markHintSeen () {
  try { window.localStorage.setItem(HINTS_KEY, String(seenCount() + 1)) } catch (e) { /* private window: hints again next time */ }
}
